/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/media-has-caption */
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { AddRounded } from '@mui/icons-material';

import api from '@src/apis';
import {
  DEFAULT_RETENTION_DAYS,
  SORT_VOICE_OPTIONS_COMMUNITY,
  SORT_VOICE_OPTIONS_FAVOURITE,
  SORT_VOICE_OPTIONS_MY_VOICE,
  SORT_VOICE_OPTIONS_VBEE,
  VOICE_LOAD_MORE_PAGINATION_LIMIT,
  VOICE_OWNERSHIP,
  VOICE_PROVIDER,
} from '@src/constants/voice';
import { useInView } from '@src/hooks/useInView';
import route from '@src/constants/route';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import actions from '@src/redux/actions';
import { PLAYING_AUDIO_TYPE, SILENCE_AUDIO_URL } from '@src/constants/tts';
import {
  FAVOURITE_VOICES,
  FAVOURITE_VOICES_ACTION,
  FAVOURITE_VOICES_LIMIT,
} from '@src/constants/voiceLibrary';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  CATEGORIES,
  SUB_STATUS,
  VOICE_CLONE_TYPE,
} from '@src/constants/voiceCloning';
import useUserInfo from '@src/hooks/useTtsUser';
import { COLOR } from '@src/styles/color';
import FirstVoiceBoxImg from '@src/assets/images/create-first-voice-box.svg';
import useAgeGroupVoiceCloning from '@src/hooks/useAgeGroupVoiceCloning';
import Voice from './Voice';
import TabsTypeVoice from './TabsTypeVoice';
import Toolbar from './Toolbar';
import { StyledLoading } from '../index.styled';
import Language from './Toolbar/Language';
import Accent from './Toolbar/Accent';
import ButtonActionDrawer from './ButtonActionDrawer';
import {
  StyledDrawer,
  StyledFilter,
  StyledFilterDrawer,
  StyledSortItem,
  StyledVoiceDrawer,
} from './index.styled';
import AdvancedFilterSection from './AdvancedFilterSection';

// ================== Constants ==================
const INITIAL_DATA_FILTER = {
  language: { name: { vi: 'Tất cả', en: 'All' }, primaryLanguageSubTag: '' },
  region: '',
  categories: [],
  ageGroup: [],
  gender: [],
  retentionDays: [],
  level: [],
  search: '',
  voiceCloningType: [],
};

// ================== Main Component ==================
const VoiceDrawer = ({
  open,
  onClose,
  onChangeVoice,
  activeVoiceId,
  openSearchPanel,
  onChangeOpenWrongSyntaxDialog,
  onShowDialogUpgradeVoiceCloning,
  onShowDialogUpgradeInstantVoiceCloning,
  defaultVoiceOwnership,
  onDeleteCommunityVoice,
  onDeleteVoiceCode,
}) => {
  const { t, i18n } = useTranslation();
  const { language: currentLang } = i18n;
  const { getFeatureValue } = useFeatureFlags();
  const dispatch = useDispatch();
  const history = useHistory();
  const { ttsUser } = useUserInfo();
  // ================== State ==================
  const [dataFilter, setDataFilter] = useState(INITIAL_DATA_FILTER);
  const [dataFilterDraft, setDataFilterDraft] = useState(INITIAL_DATA_FILTER);
  const [searchValueView, setSearchValueView] = useState('');
  const [favouriteVoices, setFavouriteVoices] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [nextCursor, setNextCursor] = useState('');
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFavouriteVoices, setLoadingFavouriteVoices] = useState(false);
  const [audioLink, setAudioLink] = useState('');
  const [openFilter, setOpenFilter] = useState(false);
  const [openSort, setOpenSort] = useState(false);
  const [sort, setSort] = useState('');
  const [audioType, setAudioType] = useState(PLAYING_AUDIO_TYPE.SILENCE);
  const [openInfoRetentionDays, setOpenInfoRetentionDays] = useState(false);

  // ================== Refs ==================
  const audioRef = createRef(null);
  const refVoices = useRef(null);
  const listRef = useRef();

  // ================== Redux Selectors ==================
  const { voice: requestVoice } = useSelector(
    (state) => state.synthesisRequest,
  );
  const { ref, inView } = useInView({ rootMargin: '100px' });
  const { filterVoice } = useSelector((state) => state.voice);
  const { user } = useSelector((state) => state.auth);

  // ================== Hooks ==================
  const { ageGroupOptions } = useAgeGroupVoiceCloning();

  // ================== Feature Flags ==================
  const userInFeatureVoiceCloning = getFeatureValue(
    FEATURE_KEYS.VOICE_CLONING,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const premiumVoiceEnabled = getFeatureValue(FEATURE_KEYS.PREMIUM_VOICE, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const gen2VietnamVoiceEnabled = getFeatureValue(
    FEATURE_KEYS.GEN2_VIETNAM_VOICE,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  // ================== Memoized Values ==================
  const isFavouriteVoices = filterVoice?.voiceOwnership === FAVOURITE_VOICES;

  const listVoices = useMemo(() => {
    if (isFavouriteVoices) {
      return favouriteVoices;
    }
    return voices;
  }, [isFavouriteVoices, favouriteVoices, voices]);

  const sortOptions = useMemo(() => {
    if (filterVoice?.voiceOwnership === VOICE_OWNERSHIP.VBEE)
      return SORT_VOICE_OPTIONS_VBEE;
    if (filterVoice?.voiceOwnership === VOICE_OWNERSHIP.PERSONAL)
      return SORT_VOICE_OPTIONS_MY_VOICE;
    if (filterVoice?.voiceOwnership === FAVOURITE_VOICES)
      return SORT_VOICE_OPTIONS_FAVOURITE;
    return SORT_VOICE_OPTIONS_COMMUNITY;
  }, [filterVoice?.voiceOwnership]);

  const dataLanguages = useMemo(() => {
    const allRegion = { name: { vi: 'Tất cả', en: 'All' }, regionSubTag: '' };
    const allLanguage = {
      name: { vi: 'Tất cả', en: 'All' },
      primaryLanguageSubTag: '',
    };

    return [allLanguage, ...languages].map((language) => {
      if (language?.primaryLanguageSubTag === 'vi') {
        return {
          ...language,
          regions: [
            allRegion,
            {
              name: { vi: 'Miền Bắc', en: 'Northern' },
              regionSubTag: 'northern',
            },
            {
              name: { vi: 'Miền Trung', en: 'Central' },
              regionSubTag: 'central',
            },
            {
              name: { vi: 'Miền Nam', en: 'Southern' },
              regionSubTag: 'southern',
            },
          ],
        };
      }
      return {
        ...language,
        regions: [allRegion, ...(language.regions || [])],
      };
    });
  }, [languages]);

  const ageGroupOptionsTranslated = ageGroupOptions?.map((item) => ({
    ...item,
    label: t(item.label),
  }));

  const advancedFilterOptions = useMemo(
    () => ({
      categories: {
        options: CATEGORIES?.map((item) => ({
          label: t(item?.value),
          value: item?.value,
        })),
        isShow: true,
      },
      gender: {
        options: [
          { label: t('male'), value: 'male' },
          { label: t('female'), value: 'female' },
        ],
        isShow: true,
      },
      ageGroup: {
        options: ageGroupOptionsTranslated,
        isShow: [VOICE_OWNERSHIP.COMMUNITY, FAVOURITE_VOICES].includes(
          filterVoice?.voiceOwnership,
        ),
      },
      retentionDays: {
        options: [
          { label: t('dayNumber', { days: 30 }), value: 30 },
          { label: t('dayNumber', { days: 90 }), value: 90 },
          { label: t('dayNumber', { days: 180 }), value: 180 },
          { label: t('yearNumber', { value: 1 }), value: 365 },
        ],
        isShow: [VOICE_OWNERSHIP.COMMUNITY].includes(
          filterVoice?.voiceOwnership,
        ),
      },
      level: {
        options: [
          { label: t('BASIC'), value: 'BASIC' },
          { label: t('ADVANCED'), value: 'ADVANCED' },
          ...(gen2VietnamVoiceEnabled
            ? [{ label: t('GEN2'), value: 'GEN2' }]
            : []),
          ...(premiumVoiceEnabled
            ? [{ label: t('PREMIUM'), value: 'PREMIUM' }]
            : []),
        ],
        isShow: [VOICE_OWNERSHIP.VBEE].includes(filterVoice?.voiceOwnership),
      },
      voiceCloningType: {
        options: [
          { label: t('proVoiceCloningOption'), value: 'professional' },
          { label: t('instantVoiceCloningOption'), value: 'instant' },
        ],
        isShow: [VOICE_OWNERSHIP.PERSONAL].includes(
          filterVoice?.voiceOwnership,
        ),
      },
    }),
    [filterVoice?.voiceOwnership, t],
  );

  // ================== Handlers ==================
  const isFiltering = useMemo(
    () =>
      dataFilter.language?.primaryLanguageSubTag ||
      dataFilter.region ||
      dataFilter.categories?.length > 0 ||
      dataFilter?.ageGroup?.length > 0 ||
      dataFilter?.gender?.length > 0 ||
      (dataFilter?.retentionDays?.length > 0 &&
        dataFilter?.retentionDays[0] !== DEFAULT_RETENTION_DAYS) ||
      dataFilter?.level?.length > 0 ||
      dataFilter?.voiceCloningType?.length > 0,
    [dataFilter],
  );

  const getInitSortBy = (ownership) => {
    if (ownership === VOICE_OWNERSHIP.VBEE) {
      return SORT_VOICE_OPTIONS_VBEE[0].value;
    }
    if (ownership === VOICE_OWNERSHIP.PERSONAL) {
      return SORT_VOICE_OPTIONS_MY_VOICE[0].value;
    }
    if (ownership === FAVOURITE_VOICES) {
      return SORT_VOICE_OPTIONS_FAVOURITE[0].value;
    }
    return SORT_VOICE_OPTIONS_COMMUNITY[0].value;
  };

  const handleCloseVoiceDrawer = () => onClose();
  // Fetch favourite voices
  const paramsGetVoicesList = (isLoadMore = false) => {
    const voiceOwnership = defaultVoiceOwnership || filterVoice?.voiceOwnership;
    const isVietnamese = dataFilter.language?.primaryLanguageSubTag === 'vi';
    const paramsFilterLanguage = {
      language: dataFilter.language?.primaryLanguageSubTag,
      ...(isVietnamese
        ? { locale: dataFilter?.region }
        : { region: dataFilter?.region }),
    };
    const paramsFilterCategory =
      dataFilter.categories?.length > 0
        ? { category: dataFilter.categories.join(',') }
        : {};
    const paramsFilterLevel =
      dataFilter.level?.length > 0 ? { level: dataFilter.level.join(',') } : {};
    const paramsFilterVoiceCloningType =
      dataFilter.voiceCloningType?.length > 0
        ? { voiceCloningType: dataFilter.voiceCloningType.join(',') }
        : {};

    const voicesList = {
      limit: VOICE_LOAD_MORE_PAGINATION_LIMIT,
      voiceOwnership,
      ...(isLoadMore && { cursor: nextCursor }),
      ...paramsFilterLanguage,
      ...(sort !== '' && {
        sort,
      }),
      ...paramsFilterCategory,
      ageGroup: dataFilter.ageGroup?.join(','),
      gender: dataFilter.gender?.join(','),
      retentionDays: dataFilter.retentionDays?.join(','),
      ...paramsFilterLevel,
      search: dataFilter.search,
      ...paramsFilterVoiceCloningType,
    };

    const favouriteVoicesList = {
      ...paramsFilterLanguage,
      ...paramsFilterCategory,
      ...(isFavouriteVoices && sort
        ? {
            sort,
          }
        : {
            sort: SORT_VOICE_OPTIONS_FAVOURITE[0].value,
          }),
      ageGroup: dataFilter.ageGroup?.join(','),
      gender: dataFilter.gender?.join(','),
      ...(dataFilter.search ? { search: dataFilter.search } : {}),
      ...(dataFilter.search
        ? { searchFields: 'name,code,description,category' }
        : {}),
      ...(dataFilter.search ? { lang: currentLang } : {}),
    };

    return { voicesList, favouriteVoicesList };
  };

  const fetchFavouriteVoices = async () => {
    setLoadingFavouriteVoices(true);
    const { favouriteVoicesList } = paramsGetVoicesList();
    const data = await api.favouriteVoices.getFavouriteVoices(
      favouriteVoicesList,
    );
    setLoadingFavouriteVoices(false);
    if (data.result) {
      setFavouriteVoices(data.result?.favouriteVoices || []);
    }
  };

  const fetchVoices = async ({ isLoadMore = false }) => {
    if (isFavouriteVoices) {
      return;
    }
    setLoading(true);

    const { voicesList } = paramsGetVoicesList(isLoadMore);
    const data = await api.voices.getVoicesV3(voicesList);

    if (onDeleteCommunityVoice) onDeleteCommunityVoice();
    if (onDeleteVoiceCode && requestVoice?.code) onDeleteVoiceCode();

    if (data.result) {
      setHasNextPage(data.result?.pagination?.hasNextPage);
      setNextCursor(data.result?.pagination?.nextCursor);
      setVoices((prev) =>
        isLoadMore
          ? [...prev, ...(data.result?.voices || [])]
          : data.result?.voices || [],
      );
    }
    setLoading(false);
  };

  const handleChangePlayAudio = (voice) => {
    dispatch(actions.audioPlayer.updateStatus(false));
    const audio = audioRef.current;
    if (!audioLink || voice.demo !== audioLink) {
      setAudioType(PLAYING_AUDIO_TYPE.SILENCE);
      if (audio) audio.src = SILENCE_AUDIO_URL;
      audio.load();
      audio.play();
      setAudioLink(voice.demo);
      audio.load();
    } else if (audioLink && voice.demo === audioLink) {
      audio.pause();
      audio.currentTime = 0;
      audio.play();
    }

    if (voice.playStatus) {
      audio.pause();
    } else {
      audio.play();
    }

    const newVoices = listVoices.map((item) =>
      item.id === voice.id
        ? { ...voice, playStatus: !voice.playStatus }
        : { ...item, playStatus: false },
    );

    if (!isFavouriteVoices) {
      setVoices(newVoices);
    } else {
      setFavouriteVoices((prev) =>
        prev.map((item) =>
          item.id === voice.id
            ? { ...voice, playStatus: !voice.playStatus }
            : { ...item, playStatus: false },
        ),
      );
    }
  };

  const handleAudioEnd = () => {
    if (audioType === PLAYING_AUDIO_TYPE.BACKGROUND_MUSIC) {
      setAudioType(PLAYING_AUDIO_TYPE.SILENCE);
      audioRef.current.currentTime = 0;
      audioRef.current.pause();
      if (!isFavouriteVoices) {
        setVoices((prev) =>
          prev.map((item) =>
            item.playStatus ? { ...item, playStatus: false } : item,
          ),
        );
      } else {
        setFavouriteVoices((prev) =>
          prev.map((item) =>
            item.playStatus ? { ...item, playStatus: false } : item,
          ),
        );
      }

      return;
    }

    if (audioLink) {
      setAudioType(PLAYING_AUDIO_TYPE.BACKGROUND_MUSIC);
      audioRef.current.src = audioLink;
      audioRef.current.load();
      audioRef.current.play();
      return;
    }

    audioRef.current.src = SILENCE_AUDIO_URL;
    audioRef.current.load();
    audioRef.current.play();
  };

  const handleSelectedVoice = (voice) => {
    const isVoiceCloning =
      voice?.provider === VOICE_PROVIDER.VBEE_VOICE_CLONING &&
      voice?.userId &&
      voice?.userId === user?.id &&
      voice?.status === SUB_STATUS.PRIVATE;
    const canUserUseVoiceCloning = validateUserPackageCanUseVoiceCloning({
      ttsUser,
      isUserInWhiteList: userInFeatureVoiceCloning,
      voice,
    });

    if (isVoiceCloning && !canUserUseVoiceCloning) {
      if (voice?.type === VOICE_CLONE_TYPE.ZERO_SHOT)
        onShowDialogUpgradeInstantVoiceCloning();
      else onShowDialogUpgradeVoiceCloning();
      return;
    }
    onChangeVoice(voice);
    handleCloseVoiceDrawer();
  };

  const handleRedirectToVoiceCloning = () => {
    history.push(route.VOICE_CLONING_VOICES);
  };

  const handleChangeVoiceOwnership = useCallback((tab) => {
    let retentionDays = [];
    const initSortBy = getInitSortBy(tab);

    if (tab === VOICE_OWNERSHIP.COMMUNITY) {
      retentionDays = [DEFAULT_RETENTION_DAYS];
    }

    setSearchValueView('');
    setDataFilterDraft({
      ...INITIAL_DATA_FILTER,
      retentionDays,
    });
    setDataFilter({
      ...INITIAL_DATA_FILTER,
      sort: initSortBy,
    });
    setSort(initSortBy);
    setFavouriteVoices([]);
  }, []);

  const handleChangeFilter = useCallback(
    (key, value) => {
      if (key === 'language') {
        const selectedLanguage = dataLanguages.find(
          (lang) => lang.primaryLanguageSubTag === value,
        );
        setDataFilterDraft((prev) => ({
          ...prev,
          region: '',
          language: selectedLanguage,
        }));
      } else {
        setDataFilterDraft((prev) => ({
          ...prev,
          [key]: value,
        }));
      }
    },
    [dataLanguages, dataFilter?.voiceOwnership],
  );

  const handleApplyFilter = useCallback(() => {
    setDataFilter({
      ...dataFilterDraft,
      search: dataFilter.search,
    });
    setOpenFilter(false);
  }, [dataFilterDraft, dataFilter.search]);

  const handleSelectFilter = useCallback((key, value) => {
    setDataFilterDraft((prev) => {
      if (key === 'retentionDays' || key === 'sort') {
        return { ...prev, [key]: [value] };
      }
      const prevArr = Array.isArray(prev[key]) ? prev[key] : [];
      const exists = prevArr.includes(value);
      return {
        ...prev,
        [key]: exists
          ? prevArr.filter((v) => v !== value)
          : [...prevArr, value],
      };
    });
  }, []);

  const handleUpdateFavouriteVoices = async ({ voiceCode, action }) => {
    const isLimitFavouriteVoices =
      (favouriteVoices?.length ?? 0) >= FAVOURITE_VOICES_LIMIT;

    if (action === FAVOURITE_VOICES_ACTION.REMOVE) {
      await api.favouriteVoices.updateFavouriteVoices({
        voiceCode,
        action,
      });
    } else if (action === FAVOURITE_VOICES_ACTION.ADD) {
      if (isLimitFavouriteVoices) {
        dispatch(
          actions.noti.push({
            severity: 'warning',
            message: t('maxFavouriteVoices', {
              numFavouriteVoices: FAVOURITE_VOICES_LIMIT,
            }),
          }),
        );
      } else {
        await api.favouriteVoices.updateFavouriteVoices({
          voiceCode,
          action,
        });
      }
    }
  };

  // ================== Effects ==================

  useEffect(() => {
    fetchFavouriteVoices();
  }, [dataFilter, isFavouriteVoices, sort]);

  useEffect(() => {
    listRef.current = {
      items: listVoices,
      loading,
      loadMore: () => fetchVoices({ isLoadMore: true }),
      hasNextPage,
    };
  }, [listVoices, loading, hasNextPage, fetchVoices]);

  useEffect(() => {
    if (
      listRef.current.items.length &&
      inView &&
      !listRef.current.loading &&
      listRef.current.hasNextPage
    ) {
      listRef.current.loadMore();
    }
  }, [inView]);

  useEffect(() => {
    const initSortBy = getInitSortBy(filterVoice?.voiceOwnership);

    setDataFilterDraft({
      ...INITIAL_DATA_FILTER,
      retentionDays:
        filterVoice?.voiceOwnership === VOICE_OWNERSHIP.VBEE ||
        filterVoice?.voiceOwnership === VOICE_OWNERSHIP.PERSONAL
          ? []
          : [DEFAULT_RETENTION_DAYS],
    });
    setSort(initSortBy);
  }, []);

  useEffect(() => {
    if (open) {
      setVoices([]);
      setFavouriteVoices([]);
      setNextCursor('');
      setHasNextPage(false);
      fetchVoices({ isLoadMore: false });
      audioRef.current?.pause();
    }
  }, [open, dataFilter, filterVoice?.voiceOwnership, sort]);

  useEffect(() => {
    const fetchLanguages = async () => {
      const data = await api.language.getLanguages({
        voiceOwnership:
          filterVoice?.voiceOwnership === VOICE_OWNERSHIP.COMMUNITY &&
          VOICE_OWNERSHIP.COMMUNITY,
      });
      if (data?.status) setLanguages(data.result?.languages || []);
    };
    fetchLanguages();
  }, [filterVoice?.voiceOwnership]);

  const noteEmptyVoices = useMemo(() => {
    if (
      isFavouriteVoices &&
      !isFiltering &&
      !searchValueView &&
      sort === SORT_VOICE_OPTIONS_FAVOURITE[0].value
    ) {
      return t('emptyFavouriteVoice');
    }
    return t('noDataVoiceList');
  }, [isFavouriteVoices, t, isFiltering, searchValueView, sort]);

  // ================== Renderers ==================
  const renderListVoice = () => (
    <>
      <Box className="drawer-title">
        <Typography variant="body-16-semibold" color={COLOR.darkGray}>
          {t('chooseVoice')}
        </Typography>
        <CloseRoundedIcon
          sx={{ color: COLOR.navyBlue }}
          onClick={handleCloseVoiceDrawer}
        />
      </Box>

      <TabsTypeVoice handleChangeVoiceOwnership={handleChangeVoiceOwnership} />
      <Toolbar
        searchValueView={searchValueView}
        setSearchValueView={setSearchValueView}
        setDataFilter={setDataFilter}
        setOpenFilter={setOpenFilter}
        isFiltering={isFiltering}
        setOpenSort={setOpenSort}
        hasSort={!!sort}
      />

      <StyledVoiceDrawer ref={refVoices} openSearchPanel={openSearchPanel}>
        <List className="list">
          <div>
            {listVoices.map((voice) => (
              <ListItem key={voice.id} className="list-item">
                <ListItemButton>
                  <Voice
                    key={voice.id}
                    isFavourite={favouriteVoices?.some(
                      (item) => item.id === voice.id,
                    )}
                    canFavouriteVoices={
                      favouriteVoices?.length < FAVOURITE_VOICES_LIMIT
                    }
                    handleUpdateFavouriteVoices={handleUpdateFavouriteVoices}
                    onSelectedVoice={handleSelectedVoice}
                    onChangePlayAudio={handleChangePlayAudio}
                    voice={voice}
                    active={voice.id === activeVoiceId}
                    onChangeOpenWrongSyntaxDialog={
                      onChangeOpenWrongSyntaxDialog
                    }
                    isMyVoice={
                      filterVoice?.voiceOwnership === VOICE_OWNERSHIP.PERSONAL
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
            <div style={{ height: '5px' }} ref={ref} />
            {listVoices?.length === 0 && !loadingFavouriteVoices && !loading && (
              <div className="empty-voices">
                <img src={FirstVoiceBoxImg} alt="first-voice-box" />
                <Typography variant="body-14-medium" color={COLOR.label2[64]}>
                  {noteEmptyVoices}
                </Typography>
              </div>
            )}
          </div>
          {(loadingFavouriteVoices || loading) && (
            <StyledLoading>
              <CircularProgress
                sx={{
                  fontSize: '24px',
                  color: COLOR.navyBlue,
                }}
              />
            </StyledLoading>
          )}
        </List>
      </StyledVoiceDrawer>
    </>
  );

  const renderFilter = () => {
    const handleGoback = () => {
      setDataFilterDraft(dataFilter);
      setOpenFilter(false);
      setOpenSort(false);
    };

    const handleResetFilterDraft = () => {
      const initSortBy = getInitSortBy(filterVoice?.voiceOwnership);

      setDataFilterDraft({
        ...INITIAL_DATA_FILTER,
        retentionDays:
          filterVoice?.voiceOwnership === VOICE_OWNERSHIP.VBEE ||
          filterVoice?.voiceOwnership === VOICE_OWNERSHIP.PERSONAL
            ? []
            : [DEFAULT_RETENTION_DAYS],
      });
      setSort(initSortBy);
    };

    if (openSort) {
      return (
        <StyledFilterDrawer>
          <Box className="drawer-title-filter">
            <ArrowBackRoundedIcon
              sx={{ color: COLOR.navyBlue }}
              onClick={handleGoback}
            />
            <span className="title">{t('sort')}</span>
          </Box>
          <div style={{ padding: '0px 16px 0 16px' }}>
            {sortOptions.map((item) => (
              <StyledSortItem
                key={item.value}
                onClick={() => {
                  setSort(item.value);
                  setOpenSort(false);
                }}
                active={sort === item.value}
              >
                <Typography
                  variant="body-14-medium"
                  color={COLOR.descriptionGray}
                >
                  {t(item.label)}
                </Typography>
              </StyledSortItem>
            ))}
          </div>
        </StyledFilterDrawer>
      );
    }

    return (
      <StyledFilterDrawer>
        <Box className="drawer-title-filter">
          <ArrowBackRoundedIcon
            sx={{ color: COLOR.navyBlue }}
            onClick={handleGoback}
          />
          <span className="title">{t('filter')}</span>
        </Box>
        {[
          VOICE_OWNERSHIP.COMMUNITY,
          VOICE_OWNERSHIP.VBEE,
          FAVOURITE_VOICES,
        ].includes(filterVoice?.voiceOwnership) && (
          <StyledFilter>
            <div className="filter-item">
              <Typography variant="body-14-medium">{t('language')}</Typography>
              <Language
                dataLanguages={dataLanguages}
                dataFilter={dataFilterDraft}
                handleChangeFilter={handleChangeFilter}
              />
            </div>
            <div className="filter-item">
              <Typography variant="body-14-medium">
                {t('regionVoice')}
              </Typography>
              <Accent
                dataFilter={dataFilterDraft}
                handleChangeFilter={handleChangeFilter}
              />
            </div>
          </StyledFilter>
        )}
        <AdvancedFilterSection
          advancedFilterOptions={advancedFilterOptions}
          dataFilterDraft={dataFilterDraft}
          handleSelectFilter={handleSelectFilter}
          t={t}
          onOpenInfoRetentionDays={() => setOpenInfoRetentionDays(true)}
        />
        <ButtonActionDrawer
          handleApplyFilter={handleApplyFilter}
          handleResetFilterDraft={handleResetFilterDraft}
        />
      </StyledFilterDrawer>
    );
  };

  // ================== Render ==================
  return (
    <StyledDrawer
      title={t('chooseVoice')}
      anchor="bottom"
      open={open}
      onClose={handleCloseVoiceDrawer}
      isOpenSort={openSort}
    >
      {openFilter || openSort ? renderFilter() : renderListVoice()}
      {!loading &&
        filterVoice?.voiceOwnership === VOICE_OWNERSHIP.PERSONAL &&
        !openFilter &&
        !openSort && (
          <div
            style={{
              position: 'sticky',
              bottom: '8px',
              width: '100%',
              height: '72px',
              background: `${COLOR.white} !important`,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <CommonButton
              onClick={handleRedirectToVoiceCloning}
              variant="containedSmall"
              customStyle={{
                borderRadius: '8px',
                border: 'none',
                height: '40px',
                width: '100%',
              }}
              textProps={{
                color: COLOR.indigo.default,
                variantText: 'body-14-semibold',
              }}
              className="create-voice-button"
              startIcon={
                <AddRounded sx={{ width: '24px', color: COLOR.navyBlue }} />
              }
            >
              {t('createVoice')}
            </CommonButton>
          </div>
        )}
      <audio ref={audioRef} onEnded={handleAudioEnd}>
        <source src={audioLink} type="audio/wav" />
      </audio>
      <NotificationDialogV3
        name={t('info')}
        title={t('info')}
        description={
          <Typography
            component="p"
            color={COLOR.descriptionGray}
            variant="body-14-regular"
          >
            {t('infoRetentionDays')}
          </Typography>
        }
        hideDangerouslySetInnerHTML
        open={openInfoRetentionDays}
        paddingHeader="12px 16px !important"
        padding="16px !important"
        paddingAction="10px 16px !important"
        onClose={() => setOpenInfoRetentionDays(false)}
        variant="null"
        fitWidth
        actionComponent={
          <CommonButton
            onClick={() => setOpenInfoRetentionDays(false)}
            variant="contained"
            customStyle={{
              borderRadius: '8px',
              height: '40px',
              width: '100%',
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.indigo.default,
            }}
          >
            {t('gotIt')}
          </CommonButton>
        }
      />
    </StyledDrawer>
  );
};

export default VoiceDrawer;
